import {
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns";

import type { IsoDateRange } from "../../../lib/adminExport/datePresets";

export type SignupRangePreset =
  | "today"
  | "yesterday"
  | "thisWeek"
  | "lastWeek"
  | "last30d"
  | "thisMonth"
  | "lastMonth"
  | "last3m"
  | "custom";

export const SIGNUP_RANGE_PRESETS: { id: SignupRangePreset; label: string }[] =
  [
    { id: "today", label: "Today" },
    { id: "yesterday", label: "Yesterday" },
    { id: "thisWeek", label: "This week" },
    { id: "lastWeek", label: "Last week" },
    { id: "last30d", label: "Last 30 days" },
    { id: "thisMonth", label: "This month" },
    { id: "lastMonth", label: "Last month" },
    { id: "last3m", label: "Last 3 months" },
    { id: "custom", label: "Custom" },
  ];

/** Weeks run Monday–Sunday. */
const WEEK = { weekStartsOn: 1 as const };

const day = (d: Date) => format(d, "yyyy-MM-dd");

export function getSignupPresetRange(
  preset: Exclude<SignupRangePreset, "custom">,
  now: Date = new Date()
): IsoDateRange {
  switch (preset) {
    case "today":
      return { startDate: day(now), endDate: day(now) };
    case "yesterday": {
      const y = subDays(now, 1);
      return { startDate: day(y), endDate: day(y) };
    }
    case "thisWeek":
      return { startDate: day(startOfWeek(now, WEEK)), endDate: day(now) };
    case "lastWeek": {
      const prev = subWeeks(now, 1);
      return {
        startDate: day(startOfWeek(prev, WEEK)),
        endDate: day(endOfWeek(prev, WEEK)),
      };
    }
    case "last30d":
      return { startDate: day(subDays(now, 29)), endDate: day(now) };
    case "thisMonth":
      return { startDate: day(startOfMonth(now)), endDate: day(now) };
    case "lastMonth": {
      const prev = subMonths(now, 1);
      return {
        startDate: day(startOfMonth(prev)),
        endDate: day(endOfMonth(prev)),
      };
    }
    case "last3m":
      return { startDate: day(subMonths(now, 3)), endDate: day(now) };
  }
}

/** Human label for the active range, e.g. "Oct 13 – Oct 19, 2026". */
export function describeRange(range: IsoDateRange): string {
  const start = new Date(`${range.startDate}T00:00:00`);
  const end = new Date(`${range.endDate}T00:00:00`);
  if (range.startDate === range.endDate)
    return format(start, "EEE, MMM d, yyyy");
  const sameYear = start.getFullYear() === end.getFullYear();
  return `${format(start, sameYear ? "MMM d" : "MMM d, yyyy")} – ${format(
    end,
    "MMM d, yyyy"
  )}`;
}
